import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import {
  DEFAULT_BIG_WINNERS_CONFIG,
  type BigWinnersConfig,
  type DurationUnit,
} from "../../../components/Configurations/casino-configuration/features/bigWinnersConfig";

export const BIG_WINNERS_CONFIG_QUERY_KEY = [
  "casinoConfiguration",
  "features",
  "bigWinners",
] as const;

/** Raw `data` shape returned by `GET /v3/big-winners`. */
export type BigWinnersConfigApi = {
  enabled: boolean;
  threshold: number;
  max_items: number;
  time_window: { value: number; unit: string };
  refresh_interval: { value: number; unit: string };
};

/**
 * Request body for `PUT /v3/big-winners`. Values may be null when the
 * operator left a field blank; the backend clamps/defaults out-of-range or
 * missing values and echoes the saved result back.
 */
export type BigWinnersConfigApiRequest = {
  enabled: boolean;
  threshold: number | null;
  max_items: number | null;
  time_window: { value: number | null; unit: DurationUnit };
  refresh_interval: { value: number | null; unit: DurationUnit };
};

function getInstance() {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const status = (error as { response?: { status?: unknown } }).response
    ?.status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Operator-facing copy from a casino-service envelope. Prefers
 * `error.message` when top-level `message` is null (the
 * `{ success: false, error: { code, message } }` shape), then the
 * fallback AppString. Axios HTTP errors (400/403) never reach
 * {@link ensureSuccess}, so callers must wrap those too.
 */
function toBigWinnersApiError(error: unknown, fallback: string): Error {
  if (error instanceof Error && responseStatus(error) === undefined) {
    return error;
  }
  return new Error(getApiErrorMessage(error, fallback));
}

function ensureSuccess(body: unknown, fallback: string): void {
  if (
    body &&
    typeof body === "object" &&
    (body as Record<string, unknown>).success === false
  ) {
    throw new Error(
      getApiErrorMessage({ response: { data: body } }, fallback),
    );
  }
}

/**
 * Coerce a backend duration unit to the UI union, falling back to the provided
 * default when the value is missing or unrecognised so a bad payload never
 * crashes the form. Legacy `minutes` payloads are treated as unrecognised and
 * fall back too, since minutes is no longer a selectable granularity.
 */
function toDurationUnit(raw: unknown, fallback: DurationUnit): DurationUnit {
  return raw === "hours" || raw === "days" ? raw : fallback;
}

function toNumberOrNull(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

/**
 * Map the snake_case API payload onto the camelCase {@link BigWinnersConfig}
 * used by the card. Missing/blank fields fall back to the domain defaults.
 */
export function mapApiToBigWinnersConfig(
  api: Partial<BigWinnersConfigApi> | null | undefined,
): BigWinnersConfig {
  if (!api) return DEFAULT_BIG_WINNERS_CONFIG;
  return {
    enabled: Boolean(api.enabled),
    threshold: toNumberOrNull(api.threshold),
    maxItems: toNumberOrNull(api.max_items),
    timeWindowValue: toNumberOrNull(api.time_window?.value),
    timeWindowUnit: toDurationUnit(
      api.time_window?.unit,
      DEFAULT_BIG_WINNERS_CONFIG.timeWindowUnit,
    ),
    refreshIntervalValue: toNumberOrNull(api.refresh_interval?.value),
    refreshIntervalUnit: toDurationUnit(
      api.refresh_interval?.unit,
      DEFAULT_BIG_WINNERS_CONFIG.refreshIntervalUnit,
    ),
  };
}

export async function fetchBigWinnersConfig(): Promise<BigWinnersConfig> {
  const instance = getInstance();
  try {
    const { data: body } = await instance.get(
      API_ROUTES.GET_BIG_WINNERS_CONFIG,
    );
    ensureSuccess(
      body,
      AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_FETCH_ERROR,
    );
    return mapApiToBigWinnersConfig(body?.data);
  } catch (error) {
    throw toBigWinnersApiError(
      error,
      AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_FETCH_ERROR,
    );
  }
}

export function useBigWinnersConfig() {
  return useQuery({
    queryKey: BIG_WINNERS_CONFIG_QUERY_KEY,
    queryFn: fetchBigWinnersConfig,
    staleTime: 60_000,
  });
}

/**
 * Map the camelCase {@link BigWinnersConfig} onto the snake_case PUT body.
 * Values are sent as-is (including null for blanks); the backend clamps them.
 */
export function mapBigWinnersConfigToApi(
  config: BigWinnersConfig,
): BigWinnersConfigApiRequest {
  return {
    enabled: config.enabled,
    threshold: config.threshold,
    max_items: config.maxItems,
    time_window: {
      value: config.timeWindowValue,
      unit: config.timeWindowUnit,
    },
    refresh_interval: {
      value: config.refreshIntervalValue,
      unit: config.refreshIntervalUnit,
    },
  };
}

/**
 * Persist the config via `PUT`. The backend clamps every value to its
 * guardrails and returns the value that was actually saved, so the mapped
 * response reflects any clamping the operator's input triggered.
 */
export async function saveBigWinnersConfig(
  config: BigWinnersConfig,
): Promise<BigWinnersConfig> {
  const instance = getInstance();
  try {
    const { data: body } = await instance.put(
      API_ROUTES.PUT_BIG_WINNERS_CONFIG,
      mapBigWinnersConfigToApi(config),
    );
    ensureSuccess(
      body,
      AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_SAVE_ERROR,
    );
    return mapApiToBigWinnersConfig(body?.data);
  } catch (error) {
    throw toBigWinnersApiError(
      error,
      AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_SAVE_ERROR,
    );
  }
}

export function useUpdateBigWinnersConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveBigWinnersConfig,
    onSuccess: (saved) => {
      // Seed the cache with the clamped value the server actually saved so
      // other consumers see the persisted config without an extra round-trip.
      queryClient.setQueryData(BIG_WINNERS_CONFIG_QUERY_KEY, saved);
    },
  });
}

/** Result of a manual `POST /big-winners/refresh`. */
export type BigWinnersRefreshResult = {
  /** Number of showcase cards produced (0 when skipped). */
  count: number;
  /** ISO-8601 timestamp of the recompute; empty string when skipped. */
  generatedAt: string;
  /** True when the feature was disabled and the recompute was skipped. */
  skipped: boolean;
};

/** Map the snake_case refresh payload onto the camelCase result. */
export function mapApiToBigWinnersRefreshResult(
  api: Record<string, unknown> | null | undefined,
): BigWinnersRefreshResult {
  return {
    count: typeof api?.count === "number" ? (api.count as number) : 0,
    generatedAt:
      typeof api?.generated_at === "string" ? (api.generated_at as string) : "",
    skipped: Boolean(api?.skipped),
  };
}

/**
 * Manually recompute the Big Winners showcase now instead of waiting for the
 * automatic interval. When the feature is disabled the backend returns
 * `skipped: true` without recomputing.
 */
export async function refreshBigWinners(): Promise<BigWinnersRefreshResult> {
  const instance = getInstance();
  try {
    const { data: body } = await instance.post(
      API_ROUTES.POST_BIG_WINNERS_REFRESH,
    );
    ensureSuccess(
      body,
      AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_ERROR,
    );
    return mapApiToBigWinnersRefreshResult(body?.data);
  } catch (error) {
    throw toBigWinnersApiError(
      error,
      AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_ERROR,
    );
  }
}

export function useRefreshBigWinners() {
  return useMutation({ mutationFn: refreshBigWinners });
}
